import { useState } from "react";

// Components
import ResultCell from "../ResultCell/ResultCell";

// Utils
import { createPartial, isOverallQuestion } from "utils/bqmq.utils";

// Styles
import { toDateAndTime } from "utils/general.utils";
import UserVotesTableContainer from "./UserVotesTable.styles";

type Props = {
    usersVotes: Vote[];
    results: Results;
    factor: string;
    setHighlightedFactor: Function;
};

type QuestionInfo = { text: string; label: string };

const getQuestionsForFactor = (
    votes: Vote[],
    factor: string,
    results: Results
): QuestionInfo[] => {
    const labels = new Map<string, string>();
    for (const vote of votes) {
        if (
            vote.question.factor.text === factor &&
            vote.choice.text !== "Comment"
        ) {
            labels.set(
                vote.question.text,
                vote.question.label ?? vote.question.text
            );
        }
    }
    return Object.keys(results)
        .filter((key) => labels.has(key))
        .map((key) => ({ text: key, label: labels.get(key)! }));
};

const setUserLastUpdated = (answers: Vote[], factor: string) => {
    const factorKey = answers.find(
        (key: Vote) =>
            key.question.factor.text.includes(factor) &&
            key.choice.text !== "Comment"
    );

    if (factorKey) {
        const date = factorKey.historic_date
            ? factorKey.historic_date
            : factorKey.created;
        return toDateAndTime(date);
    }
    return "Not voted";
};

const groupObjects = (usersVotes: Vote[]) => {
    return usersVotes.reduce((acc: GroupedVotes, obj: Vote) => {
        if (obj.analyst) {
            acc[obj.analyst.name] = [...(acc[obj.analyst.name] || []), obj];
        }
        return acc;
    }, {});
};

const setHeaders = (
    highlightedColumn: string,
    setHighlightedColumn: Function,
    setHighlightedFactor: Function,
    questions: QuestionInfo[]
) =>
    questions.map((q: QuestionInfo) => {
        return (
            <div
                className={
                    "question " +
                    (q.text === highlightedColumn ? "highlight " : "") +
                    (isOverallQuestion(q.text) ? "overall " : "")
                }
                key={q.text}
                onMouseEnter={() => {
                    setHighlightedColumn(q.text);
                    setHighlightedFactor(q.text);
                }}
                onMouseLeave={() => {
                    setHighlightedColumn("");
                    setHighlightedFactor("");
                }}
            >
                <p>{q.label}</p>
            </div>
        );
    });

const addResultsCell = (
    factor: string,
    highlightedColumn: string,
    setHighlightedColumn: Function,
    setHighlightedFactor: Function,
    questions: QuestionInfo[],
    votes: Vote[]
) => {
    return questions.map(({ text: questionKey }: QuestionInfo) => {
        const matchingVote = votes.find(
            (vote: Vote) => vote.question.text === questionKey
        );
        if (matchingVote) {
            return (
                matchingVote.question.text !== "mode" &&
                matchingVote.question.factor.text === factor &&
                matchingVote.choice.text !== "Comment" && (
                    <div
                        className={
                            "answerCell " +
                            (matchingVote.question.text === highlightedColumn
                                ? "highlight "
                                : "") +
                            (isOverallQuestion(matchingVote.question.text)
                                ? "overall"
                                : "")
                        }
                        key={matchingVote.question.text}
                        onMouseEnter={() => {
                            setHighlightedColumn(matchingVote.question.text);
                            setHighlightedFactor(matchingVote.question.text);
                        }}
                        onMouseLeave={() => {
                            setHighlightedColumn("");
                            setHighlightedFactor("");
                        }}
                    >
                        <ResultCell
                            value={matchingVote.choice.text
                                .toString()
                                .toUpperCase()}
                        />
                    </div>
                )
            );
        }
        return (
            <div
                className={
                    "answerCell " +
                    (questionKey === highlightedColumn ? "highlight " : "") +
                    (isOverallQuestion(questionKey) ? "overall" : "")
                }
                key={questionKey}
                onMouseEnter={() => {
                    setHighlightedColumn(questionKey);
                    setHighlightedFactor(questionKey);
                }}
                onMouseLeave={() => {
                    setHighlightedColumn("");
                    setHighlightedFactor("");
                }}
            >
                <ResultCell value={{ value: "" }} />
            </div>
        );
    });
};

const addRow = (
    usersVotes: Vote[],
    resultCellAdder: Function,
    factor: string
) => {
    return Object.entries(groupObjects(usersVotes))
        .sort((a: Result, b: Result) => a[0].localeCompare(b[0]))
        .map(([key, votes]) => (
            <div className={"userRow"} key={key}>
                <div className="user-details-wrapper">
                    <div className={"name"}>{`${key}`}</div>
                    <div className={"name"}>{`${setUserLastUpdated(
                        votes as Vote[],
                        factor as string
                    )}`}</div>
                </div>

                {resultCellAdder(votes)}
            </div>
        ));
};

export default function UserVotesTable({
    usersVotes,
    results,
    factor,
    setHighlightedFactor,
}: Props) {
    const [highlightedColumn, setHighlightedColumn] = useState<string>("");
    const questions = getQuestionsForFactor(usersVotes, factor, results);

    return (
        <UserVotesTableContainer>
            <div
                className={"questionsRow " + (factor === "FADE" ? "short" : "")}
            >
                <div className={"name"}></div>

                {setHeaders(
                    highlightedColumn,
                    setHighlightedColumn,
                    setHighlightedFactor,
                    questions
                )}
            </div>

            {addRow(
                usersVotes,
                createPartial(
                    addResultsCell,
                    factor,
                    highlightedColumn,
                    setHighlightedColumn,
                    setHighlightedFactor,
                    questions
                ),
                factor
            )}
        </UserVotesTableContainer>
    );
}
